<template>
  <div class="abres">
    <div class="abres__cards">
      <div
        v-for="tile in tiles"
        :key="tile.rowIndex"
        class="abres__card"
      >
        <div class="abres__variant">
          {{ tile.label }}
        </div>
        <div
          class="abres__stage"
          @mouseenter="hovered = tile.rowIndex"
          @mouseleave="hovered = null"
        >
          <!-- Busy: regenerating OR saving — keep the previous image, blurred, with an overlay -->
          <template v-if="(tile.state === 'generating' && tile.url) || (tile.state === 'done' && tile.saving && tile.url)">
            <img
              :src="tile.url"
              class="abres__img abres__img--regen"
              :class="{ 'abres__img--contain': tile.mode === 'original' }"
              :alt="tile.label"
            >
            <div class="abres__regen">
              <span class="abres__regen-pill">{{ tile.saving ? 'Saving' : 'Regenerating' }} <span class="abres__dots">···</span></span>
            </div>
          </template>
          <!-- Done: image + hover actions (download / save) -->
          <template v-else-if="tile.state === 'done' && tile.url">
            <img
              :src="tile.url"
              class="abres__img"
              :class="{ 'abres__img--contain': tile.mode === 'original' }"
              :alt="tile.label"
            >
            <div
              v-show="hovered === tile.rowIndex"
              class="abres__imgactions"
            >
              <button
                type="button"
                class="abres__iconbtn"
                title="Download"
                @click="download(tile.rowIndex)"
              >
                <i class="bi bi-download" />
              </button>
              <button
                type="button"
                class="abres__iconbtn"
                title="Save to File Browser"
                @click="save(tile.rowIndex)"
              >
                <i class="bi bi-folder-plus" />
              </button>
            </div>
          </template>
          <!-- Error -->
          <div
            v-else-if="tile.state === 'error'"
            class="abres__error"
          >
            <i class="bi bi-exclamation-triangle abres__error-icon" />
            <span class="abres__error-text">Couldn't generate</span>
            <button
              type="button"
              class="abres__retry"
              :disabled="anyGenerating"
              :title="anyGenerating ? 'Wait for the other variant to finish generating' : 'Retry'"
              @click="regen(tile.rowIndex)"
            >
              <i class="bi bi-arrow-clockwise" /> Retry
            </button>
          </div>
          <!-- Initial generation -->
          <div
            v-else
            class="abres__placeholder"
          >
            <i class="bi bi-stars abres__spark" />
            <button
              v-if="tile.slow"
              type="button"
              class="abres__retry abres__retry--slow"
              @click="regen(tile.rowIndex)"
            >
              <i class="bi bi-arrow-clockwise" /> Taking a while — Retry
            </button>
          </div>
        </div>
        <!-- Prompt editor: stays mounted below the stage through the WHOLE lifecycle of a
             variant — queued, generating and done — so the prompt is visible under the
             image while it generates and the card doesn't jump in height when the result
             lands (no layout shift). Pre-filled with the variant's prompt. While a
             (re)generation is in flight the textarea is dimmed (opacity-40) and non-editable
             and every button inside is disabled; it becomes editable only once the tile is
             done, when the user can tweak it and click Regenerate. (POR-13385-3, POR-13595) -->
        <template v-if="showPromptEditor(tile)">
          <textarea
            :ref="`editTa-${tile.rowIndex}`"
            v-model="editPrompt[tile.rowIndex]"
            class="abres__captionedit"
            placeholder="Describe what to change, then click Regenerate…"
            :disabled="tile.state !== 'done'"
          />
          <div class="abres__cardfoot">
            <!-- Version history: all generations of this variant, newest first. A fixed
                 window of thumbnails with ‹ › paging keeps each one big enough to see no
                 matter how many exist; clicking one selects it as the current image (the
                 in-use one carries a check). Regenerate sits on its own row below. (POR-13544) -->
            <div
              v-if="versions(tile).length > 1"
              class="abres__history"
            >
              <span class="abres__history-label">Previous</span>
              <button
                v-if="historyPaged(tile)"
                type="button"
                class="abres__historynav"
                :disabled="tile.state !== 'done' || !canHistoryPrev(tile)"
                title="Newer versions"
                @click.stop="pageHistory(tile.rowIndex, -1)"
              >
                <i class="bi bi-chevron-left" />
              </button>
              <div class="abres__history-strip">
                <button
                  v-for="h in historyWindow(tile)"
                  :key="h"
                  type="button"
                  class="abres__historythumb"
                  :class="{ 'abres__historythumb--active': h === tile.url }"
                  :disabled="tile.state !== 'done'"
                  :title="h === tile.url ? 'Current version' : 'Use this version'"
                  @click.stop="select(tile.rowIndex, h)"
                >
                  <img
                    :src="historyThumb(h)"
                    alt=""
                  >
                  <span
                    v-if="h === tile.url"
                    class="abres__historycheck"
                  ><i class="bi bi-check-lg" /></span>
                </button>
              </div>
              <button
                v-if="historyPaged(tile)"
                type="button"
                class="abres__historynav"
                :disabled="tile.state !== 'done' || !canHistoryNext(tile)"
                title="Older versions"
                @click.stop="pageHistory(tile.rowIndex, 1)"
              >
                <i class="bi bi-chevron-right" />
              </button>
            </div>
            <div class="abres__regenrow">
              <!-- Save or reuse this variant's prompt via the Clipboard. Opens the sidebar
                   bound to THIS regenerate box (Prompt tab pre-selected). Lucide `sparkles`
                   inlined as SVG to match the source design. (POR-13563-2) -->
              <button
                type="button"
                class="abres__sparkbtn"
                :disabled="tile.state !== 'done'"
                title="Save or reuse a prompt"
                @click="openPromptClipboard(tile)"
              >
                <svg
                  class="abres__spark-svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />
                  <path d="M20 3v4" />
                  <path d="M22 5h-4" />
                  <path d="M4 17v2" />
                  <path d="M5 18H3" />
                </svg>
              </button>
              <button
                type="button"
                class="abres__regenbtn"
                :disabled="tile.state !== 'done' || anyGenerating"
                :title="anyGenerating && tile.state === 'done' ? 'Wait for the other variant to finish generating' : 'Regenerate this variant'"
                @click="onRegenerate(tile)"
              >
                <i class="bi bi-arrow-counterclockwise" /> Regenerate
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="abres__footer">
      <button
        type="button"
        class="abres__createlayout"
        :disabled="!canCreateLayout"
        title="Create an editable layout from the generated image(s)"
        @click="createLayout"
      >
        <i class="bi bi-columns-gap" /> Create layout
      </button>
      <button
        type="button"
        class="abres__optix"
        :disabled="!canTest"
        @click="act"
      >
        A/B test in Optix&#x2122; <i class="bi bi-arrow-right" />
      </button>
    </div>
  </div>
</template>

<script>
// How many history thumbnails are visible at once; the rest are reached via ‹ › paging
// so thumbnails never shrink as more versions accumulate. (POR-13544)
const HISTORY_WINDOW = 3;

export default {
  name: 'AIChatWidgetAbHeroResult',
  props: {
    grid: { type: Object, required: true },
    // Index of the chat message this result belongs to, so a Clipboard prompt inserted
    // from the sidebar can be routed back to the correct variant's regenerate box. (POR-13563-2)
    messageIndex: { type: Number, default: null },
  },
  data() {
    return {
      hovered: null,
      // rowIndex -> the editable prompt text for that variant. Seeded from each
      // tile's description so the editbox is pre-filled and always editable. (POR-13385-3)
      editPrompt: {},
      // rowIndex -> the index of the first visible history thumbnail (the ‹ › window
      // offset). View-only; not persisted. (POR-13544)
      historyOffset: {},
    };
  },
  computed: {
    tiles() { return (this.grid && this.grid.tiles) || []; },
    // True while ANY variant is mid-(re)generation. Used to lock every variant's
    // Regenerate/Retry so the user can't start a second generation on top of an
    // in-flight one: regenerating one variant while the other is still doing its first
    // generation piles a heavy concurrent design job onto the queue, and on a
    // capacity-limited backend that starves the other variant past its per-attempt
    // timeout until it retries out and fails ("Couldn't generate"). Only one generation
    // runs at a time now; the buttons re-enable the moment everything settles. (POR-13595)
    anyGenerating() {
      return this.tiles.some((t) => t.state === 'generating');
    },
    canTest() {
      return this.tiles.length === 2 && this.tiles.every((t) => t.state === 'done' && t.url);
    },
    canCreateLayout() {
      // Disabled while any variant is (re)generating — you can't build a layout from an
      // image that's still changing, so it mirrors the Optix button and stays unclickable
      // until everything settles. Once nothing is in flight it re-enables as long as at
      // least one variant produced an image, so a single failed variant doesn't block the
      // other. (POR-13595)
      if (this.tiles.some((t) => t.state === 'generating')) return false;
      return this.tiles.some((t) => t.state === 'done' && t.url);
    },
  },
  watch: {
    // Seed/refresh each variant's editbox with its prompt. Only fills entries that
    // don't exist yet, so a user's in-progress edit is never clobbered by a re-render. (POR-13385-3)
    tiles: {
      immediate: true,
      handler(tiles) {
        (tiles || []).forEach((t) => {
          if (this.editPrompt[t.rowIndex] == null) {
            this.$set(this.editPrompt, t.rowIndex, t.description || '');
          }
        });
      },
    },
  },
  mounted() {
    // Receive a Clipboard prompt the user picked in the sidebar after opening it from a
    // variant's sparkle icon; the parent broadcasts it and only the matching result/tile
    // applies it into its regenerate box. (POR-13563-2)
    this.$root.$on('abhero:insert-prompt', this.onInsertPrompt);
  },
  beforeDestroy() {
    this.$root.$off('abhero:insert-prompt', this.onInsertPrompt);
  },
  methods: {
    // Whether to render the prompt editor (textarea + regenerate row) below the stage.
    // It's shown from the moment a variant is queued/generating — not just once done — so
    // the prompt is visible under the image while it generates and the card keeps a stable
    // height (no layout shift when the result arrives). It's disabled + dimmed until done
    // (see the :disabled bindings and .abres__captionedit:disabled). (POR-13595)
    showPromptEditor(tile) {
      return tile.state === 'done' || tile.state === 'generating' || tile.state === 'queued';
    },
    // Open the Clipboard sidebar bound to THIS variant's regenerate box: the parent
    // pre-selects the Prompt tab, seeds "Save current" with the current text, and routes
    // any prompt the user picks back into this same box. (POR-13563-2)
    openPromptClipboard(tile) {
      const ri = tile.rowIndex;
      const raw = this.editPrompt[ri] != null ? this.editPrompt[ri] : (tile.description || '');
      this.$emit('action', `ab_hero_prompt_clipboard:${JSON.stringify({
        kind: 'ab_result',
        messageIndex: this.messageIndex,
        key: ri,
        text: String(raw || ''),
      })}`);
    },
    // A prompt was picked in the Clipboard sidebar for this result → replace the target
    // variant's regenerate-box text. Only the addressed result/tile reacts. (POR-13563-2)
    onInsertPrompt(p) {
      if (!p || p.kind !== 'ab_result' || p.messageIndex !== this.messageIndex) return;
      if (!this.tiles.some((t) => t.rowIndex === p.key)) return;
      this.$set(this.editPrompt, p.key, String(p.text || ''));
    },
    act() {
      if (!this.canTest) return;
      const urls = this.tiles.map((t) => t.url);
      this.$emit('action', `ab_hero_optix:${JSON.stringify({ urls })}`);
    },
    // Retry path used by the error / "taking a while" states — re-runs the variant
    // in place with its current prompt. (POR-13385-2)
    regen(rowIndex) {
      this.$emit('action', `ab_hero_retry:${JSON.stringify({ rowIndex })}`);
    },
    // Single-click Regenerate: the editbox is already editable and pre-filled, so a
    // click just fires the regeneration with whatever prompt the box currently holds.
    // The store flips the tile to "Regenerating…" synchronously. (POR-13385-3)
    onRegenerate(tile) {
      const ri = tile.rowIndex;
      const raw = this.editPrompt[ri] != null ? this.editPrompt[ri] : (tile.description || '');
      const prompt = String(raw).trim();
      this.$emit('action', `ab_hero_retry:${JSON.stringify({ rowIndex: ri, prompt })}`);
    },
    // All versions of a variant, newest first, current included so it can carry the
    // in-use check. Stable order (the store keeps history newest-first); the legacy/seed
    // fallback prepends the current url if an older tile didn't store it. (POR-13544)
    versions(tile) {
      const url = tile && tile.url;
      const hist = (tile && Array.isArray(tile.history)) ? tile.history.filter(Boolean) : [];
      const list = (url && !hist.includes(url)) ? [url, ...hist] : hist;
      return [...new Set(list)];
    },
    historyPaged(tile) {
      return this.versions(tile).length > HISTORY_WINDOW;
    },
    // Clamped window offset for a tile (so it stays valid if history shrank/grew). (POR-13544)
    historyOffsetFor(tile) {
      const max = Math.max(0, this.versions(tile).length - HISTORY_WINDOW);
      const raw = this.historyOffset[tile.rowIndex] || 0;
      return Math.min(Math.max(0, raw), max);
    },
    historyWindow(tile) {
      const off = this.historyOffsetFor(tile);
      return this.versions(tile).slice(off, off + HISTORY_WINDOW);
    },
    canHistoryPrev(tile) {
      return this.historyOffsetFor(tile) > 0;
    },
    canHistoryNext(tile) {
      return this.historyOffsetFor(tile) + HISTORY_WINDOW < this.versions(tile).length;
    },
    pageHistory(rowIndex, dir) {
      const tile = this.tiles.find((t) => t.rowIndex === rowIndex);
      if (!tile) return;
      const max = Math.max(0, this.versions(tile).length - HISTORY_WINDOW);
      const next = Math.min(Math.max(0, this.historyOffsetFor(tile) + dir), max);
      this.$set(this.historyOffset, rowIndex, next);
    },
    // Small same-origin thumbnail for a history version. A generated variant url is
    // usually a /share link, so ask it for a downscaled WebP rather than rendering the
    // full-size image at thumbnail size. Other url shapes (data/blob/storage) pass through. (POR-13544)
    historyThumb(url) {
      if (!url || typeof url !== 'string') return url;
      if (url.startsWith('data:') || url.startsWith('blob:')) return url;
      if (url.indexOf('/share') === -1 || /[?&](lightweight|scale|is_thumbnail)=/.test(url)) return url;
      return `${url}${url.indexOf('?') === -1 ? '?' : '&'}lightweight=1&scale=256&format=webp`;
    },
    // Select a history version as the current variant image (a pure pointer move in the
    // store — no regeneration, no reordering of the strip). (POR-13544)
    select(rowIndex, url) {
      if (!url) return;
      this.$emit('action', `ab_hero_select:${JSON.stringify({ rowIndex, url })}`);
    },
    download(rowIndex) {
      this.$emit('action', `ab_hero_download:${JSON.stringify({ rowIndex })}`);
    },
    save(rowIndex) {
      this.$emit('action', `ab_hero_save:${JSON.stringify({ rowIndex })}`);
    },
    createLayout() {
      if (!this.canCreateLayout) return;
      this.$emit('action', 'ab_hero_create_layout');
    },
  },
};
</script>

<style scoped>
.abres { background: transparent; padding: 0; }
.abres__cards { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; align-items: stretch; }
/* Flex column so the stage stays a fixed square and the caption wraps BELOW it
   without ever changing the image size — both variants render identical even when
   one caption is much longer than the other. (POR-13385) */
.abres__card { display: flex; flex-direction: column; background: #fff; border: 1px solid #e3e9e7; border-radius: 10px; padding: 10px; }
.abres__variant { font-size: 12px; font-weight: 600; color: #2b3a36; margin-bottom: 6px; }
/* flex: 0 0 auto + aspect-ratio:1 → the stage is always a 1:1 square of the card
   width and never stretches/shrinks to the card height, so both stages match. */
.abres__stage { position: relative; width: 100%; aspect-ratio: 1; flex: 0 0 auto; background: #dfe8e5; border-radius: 8px; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.abres__img { width: 100%; height: 100%; object-fit: cover; }
/* "Use original" tiles show the raw source packshot, which is often non-square — fit
   the WHOLE product inside the square stage instead of cropping it. (POR-13544) */
.abres__img--contain { object-fit: contain; background: #fff; }
.abres__img--regen { filter: blur(4px) brightness(.97); }
.abres__regen { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.abres__regen-pill { background: rgba(255, 255, 255, .85); color: #2b3a36; font-size: 13px; font-weight: 600; padding: 5px 12px; border-radius: 8px; }
/* Download / Save icons over the image, shown on hover (mirrors the PDP grid). */
.abres__imgactions { position: absolute; right: 8px; bottom: 8px; display: flex; gap: 6px; }
.abres__iconbtn { width: 30px; height: 30px; border-radius: 8px; background: rgba(255, 255, 255, .95); border: 1px solid #e3e9e7; color: #2b3a36; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-size: 15px; box-shadow: 0 1px 4px rgba(0, 0, 0, .12); }
.abres__iconbtn:hover { color: #1f8a6d; border-color: #1f8a6d; }
.abres__placeholder { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 8px; }
/* inline-block so the rotate transform applies — an inline <i> would not spin (POR-13385) */
.abres__placeholder .abres__spark { display: inline-block; font-size: 26px; color: #1f8a6d; animation: spin 1.4s linear infinite; }
.abres__retry--slow { background: rgba(255, 255, 255, .92); font-size: 12px; }
.abres__error { display: flex; flex-direction: column; align-items: center; gap: 6px; color: #6b7c77; font-size: 13px; text-align: center; padding: 8px; }
.abres__error-icon { font-size: 22px; color: #b08a3e; }
.abres__retry { background: #fff; border: 1px solid #cdd6d3; color: #1f8a6d; border-radius: 8px; padding: 5px 14px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; }
.abres__retry:hover:not(:disabled) { border-color: #1f8a6d; }
.abres__retry:disabled { opacity: .5; cursor: default; }
/* Wrap the caption onto as many lines as it needs; it sits below the fixed-size
   stage so longer text never resizes the image. (POR-13385) */
/* Always-visible prompt editor. flex:1 makes both columns' editboxes fill the same
   remaining height (cards are equal-height via the grid's align-items:stretch and the
   stage above is a fixed square), so the Regenerate buttons line up at the same Y in
   both columns and long text scrolls inside the box instead of breaking the card. (POR-13385-3) */
/* resize: vertical lets the user drag the editbox taller for long prompts (POR-13544). */
.abres__captionedit { flex: 1 1 auto; width: 100%; box-sizing: border-box; margin-top: 10px; min-height: 84px; font-size: 12px; color: #2b3a36; line-height: 1.45; border: 1px solid #cdd6d3; border-radius: 8px; padding: 10px 12px; resize: vertical; overflow-y: auto; font-family: inherit; outline: none; background: #fff; }
.abres__captionedit:focus { border-color: #1f8a6d; box-shadow: 0 0 0 2px rgba(31, 138, 109, .15); }
/* While (re)generating the box is disabled: fade it to 40% and drop the focus ring so the
   pre-filled prompt reads as visible-but-not-editable under the generating image. It can't
   receive focus while disabled, so no focus ring appears either. (POR-13595) */
.abres__captionedit:disabled { background: #f7f9f8; color: #51635d; cursor: default; opacity: .4; box-shadow: none; }
/* Right-aligned, content-width Regenerate (not full-width) per QA mock. The card is
   still equal-height via the grid, so both columns' buttons stay on the same Y. (POR-13385-3) */
/* History row on top (label + ‹ › paged window of thumbnails), Regenerate on its own
   row below — per QA mock. (POR-13544) */
.abres__cardfoot { margin-top: 10px; display: flex; flex-direction: column; gap: 8px; }
.abres__history { display: flex; align-items: center; gap: 6px; min-width: 0; }
.abres__history-label { font-size: 11px; font-weight: 600; color: #9aa5a1; flex: 0 0 auto; }
.abres__history-strip { display: flex; align-items: center; gap: 5px; }
/* ‹ › paging buttons; a fixed window keeps thumbnails full-size no matter how many. */
.abres__historynav { flex: 0 0 auto; width: 20px; height: 28px; padding: 0; border: 1px solid #cdd6d3; border-radius: 6px; background: #fff; color: #51635d; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; }
.abres__historynav:hover:not(:disabled) { border-color: #1f8a6d; color: #1f8a6d; }
.abres__historynav:disabled { opacity: .4; cursor: default; }
/* Sparkles on the left, Regenerate on the right (POR-13563-2). */
.abres__regenrow { display: flex; justify-content: space-between; align-items: center; }
/* Save/reuse-prompt sparkle: quiet icon button, matching the picker's icon colour + hover. */
.abres__sparkbtn { background: none; border: none; color: #8a97a0; cursor: pointer; padding: 4px; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; }
.abres__sparkbtn:hover:not(:disabled) { color: #1f8a6d; }
.abres__sparkbtn:disabled { opacity: .5; cursor: default; }
.abres__spark-svg { display: block; }
.abres__historythumb { position: relative; width: 40px; height: 40px; flex: 0 0 auto; padding: 0; border: 1px solid #cdd6d3; border-radius: 6px; overflow: hidden; background: #fff; cursor: pointer; line-height: 0; }
.abres__historythumb:hover:not(:disabled) { border-color: #1f8a6d; }
.abres__historythumb:disabled { opacity: .5; cursor: default; }
.abres__historythumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
/* The in-use version: green ring + a corner check, so it's clear which one is selected. */
.abres__historythumb--active { border-color: #1f8a6d; box-shadow: inset 0 0 0 2px #1f8a6d; }
.abres__historycheck { position: absolute; top: 2px; right: 2px; width: 14px; height: 14px; border-radius: 50%; background: #1f8a6d; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 9px; line-height: 1; }
/* Default: white outline. Hover: fill green (per QA mock). (POR-13385-3) */
.abres__regenbtn { background: #fff; border: 1px solid #cdd6d3; color: #2b3a36; border-radius: 8px; padding: 6px 14px; font-size: 12px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; transition: background .12s ease, border-color .12s ease, color .12s ease; }
.abres__regenbtn:hover:not(:disabled) { background: #1f8a6d; border-color: #1f8a6d; color: #fff; }
.abres__regenbtn:disabled { opacity: .5; cursor: default; }
.abres__footer { display: flex; justify-content: flex-end; align-items: center; gap: 6px; margin-top: 12px; }
.abres__createlayout { background: transparent; border: none; color: #9aa5a1; border-radius: 8px; padding: 8px 12px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.abres__createlayout:hover:not(:disabled) { color: #51635d; }
.abres__createlayout:disabled { opacity: .45; cursor: default; }
.abres__optix { background: #1f8a6d; border: none; color: #fff; border-radius: 8px; padding: 8px 18px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.abres__optix:disabled { background: #a7c9bd; cursor: default; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
